import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Sparkles, TrendingUp, Landmark, Pickaxe, Leaf, Wheat, Building2, ChevronRight } from "lucide-react";

const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Metric = ({ label, value, accent = BLUE }) => (
  <div className="rounded-sm border px-2 py-1.5" style={{ borderColor: accent + "40", background: "rgba(7,27,43,0.55)" }}>
    <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">{label}</div>
    <div className="text-[13.5px] font-bold text-[#F6F8FA]" style={{ color: accent }}>{value}</div>
  </div>
);

export const Province360 = () => {
  const [list, setList] = useState([]);
  const [province, setProvince] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/cadastre360/phase-xv/province360`, { headers: auth() });
        setList(r.data.provinces);
        setProvince(r.data.provinces?.[0]?.province || "Harare");
      } catch (e) { toast.error("Province360 load failed — " + e.message); }
    })();
  }, []);

  useEffect(() => {
    if (!province) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/cadastre360/phase-xv/province360/${encodeURIComponent(province)}`, { headers: auth() });
        setDetail(r.data);
      } catch (e) { toast.error("Province detail failed — " + e.message); }
    })();
  }, [province]);

  return (
    <div className="p-6 space-y-4" data-testid="page-province360">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: GREEN + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${GREEN}30 100%)` }}>
        <MapPin size={38} className="mt-1" style={{ color: GREEN }} />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: GREEN }}>
            Cadastre360™ · Phase XV · Province360™
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Provincial Executive Operating Centres
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Every province · Situation Room · Dashboard · Executive Brief · Investment · Natural Resources ·
            Infrastructure · Revenue — all consuming existing operational systems.
          </div>
        </div>
      </div>

      <Card title={`Provinces overview · ${list.length}`} accent={GOLD}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {list.map((p) => (
            <button key={p.province} onClick={() => setProvince(p.province)}
                    className="rounded-sm border p-2 text-left"
                    style={{ borderColor: (province === p.province ? GREEN : GOLD) + "50",
                              background: province === p.province ? GREEN + "20" : "rgba(7,27,43,0.55)" }}
                    data-testid={`province-btn-${p.province.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="text-[12.5px] font-semibold text-[#F6F8FA]">{p.province}</div>
              <div className="text-[10.5px] text-[#B3C2CC]">Economy {p.economy_score} · Risks {p.risks_open}</div>
              <div className="text-[10.5px] text-[#7C93A5] font-mono">USD {(p.investment_usd/1_000_000).toFixed(0)}M pipeline</div>
            </button>
          ))}
        </div>
      </Card>

      {detail && (
        <>
          <div className="rounded-sm border p-3 flex items-center gap-2"
               style={{ borderColor: GREEN + "40", background: "rgba(11,53,88,0.45)" }}>
            <Sparkles size={14} style={{ color: GREEN }} />
            <div className="text-[12.5px] text-[#F6F8FA]">{detail.situation_summary}</div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card title="Provincial Economy · Investment · Revenue" accent={GOLD} testId="province-economy">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <Metric label="Economy score"     value={detail.deck.provincial_economy.score} accent={GOLD} />
                <Metric label="GDP contribution"  value={detail.deck.provincial_economy.gdp_contribution_pct + "%"} accent={GOLD} />
                <Metric label="Investment"        value={`USD ${(detail.deck.provincial_investment.pipeline_usd/1_000_000).toFixed(0)}M`} accent={GREEN} />
                <Metric label="Revenue collected" value={`USD ${(detail.deck.revenue.collected_usd/1_000_000).toFixed(1)}M`} accent={BLUE} />
                <Metric label="Revenue outstanding" value={`USD ${(detail.deck.revenue.outstanding_usd/1_000_000).toFixed(1)}M`} accent={BLUE} />
                <Metric label="Population"        value={detail.deck.population.population.toLocaleString()} accent={PURPLE} />
              </div>
            </Card>

            <Card title="Land · Mining · Agriculture · Environment" accent={TEAL} testId="province-natural">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <Metric label="Parcels"           value={detail.deck.land.parcels.toLocaleString()} accent={GOLD} />
                <Metric label="Land utilisation"  value={detail.deck.land.utilisation_pct + "%"} accent={GOLD} />
                <Metric label="Mining rights"     value={detail.deck.mining.active_rights} accent={GOLD} />
                <Metric label="Royalties"         value={`USD ${(detail.deck.mining.royalties_usd/1_000_000).toFixed(1)}M`} accent={GOLD} />
                <Metric label="Agri ha"           value={detail.deck.agriculture.hectares.toLocaleString()} accent={GREEN} />
                <Metric label="Env score"         value={detail.deck.environment.score} accent={TEAL} />
                <Metric label="Protected ha"      value={detail.deck.environment.protected_ha.toLocaleString()} accent={TEAL} />
                <Metric label="Natural resource idx" value={detail.deck.natural_resources.resource_index} accent={TEAL} />
              </div>
            </Card>

            <Card title="Infrastructure · Tourism · Service Delivery · Digital Government" accent={BLUE}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <Metric label="Infra score"       value={detail.deck.infrastructure.score} accent={BLUE} />
                <Metric label="Infra projects"    value={detail.deck.infrastructure.projects} accent={BLUE} />
                <Metric label="Tourism sites"     value={detail.deck.tourism.attractions} accent={GREEN} />
                <Metric label="Visitors YoY"      value={detail.deck.tourism.visitors_yoy_pct + "%"} accent={GREEN} />
                <Metric label="Service delivery"  value={detail.deck.service_delivery.score} accent={PURPLE} />
                <Metric label="Digital gov"       value={detail.deck.digital_government.score} accent={PURPLE} />
              </div>
            </Card>

            <Card title="Strategic Projects · Risks" accent={PURPLE}>
              <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">Projects</div>
              <ul className="text-[12px] text-[#F6F8FA] list-disc pl-4 mb-2">
                {detail.deck.strategic_projects.map((p) => <li key={p}>{p}</li>)}
              </ul>
              <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">Risks</div>
              <div className="flex flex-wrap gap-1">
                {detail.deck.risks.map((r) => (
                  <span key={r} className="rounded-sm px-1.5 py-0.5 text-[10.5px] font-mono uppercase"
                        style={{ background: "#E06C6C25", color: "#E06C6C", border: "1px solid #E06C6C45" }}>{r}</span>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default Province360;
